<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import CheckboxAttribute from 'dashboard/components-next/CustomAttributes/CheckboxAttribute.vue';

defineProps({
  selectedCount: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['add', 'close']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const selectedAttributes = ref([]);
const attributes = useMapGetter('attributes/getContactAttributes');
const popoverStyle = ref({ top: '0px', right: '0px' });

const hasContactAttributes = computed(() => attributes.value?.length > 0);

const inputTypes = ['text', 'number', 'link', 'date', 'currency', 'percent'];

const updatePopoverPosition = () => {
  const container = document.querySelector('.bulk-action__container');
  if (!container) return;

  const rect = container.getBoundingClientRect();
  popoverStyle.value = {
    top: `${rect.bottom + 8}px`,
    right: `${window.innerWidth - rect.right}px`,
  };
};

onMounted(async () => {
  try {
    await store.dispatch('attributes/get');
  } catch {
    // Silently handle error
  }
  updatePopoverPosition();
  window.addEventListener('scroll', updatePopoverPosition, true);
  window.addEventListener('resize', updatePopoverPosition);
});

onUnmounted(() => {
  window.removeEventListener('scroll', updatePopoverPosition, true);
  window.removeEventListener('resize', updatePopoverPosition);
});

const toggleAttribute = attribute => {
  const existingIndex = selectedAttributes.value.findIndex(
    attr => attr.key === attribute.attributeKey
  );

  if (existingIndex > -1) {
    selectedAttributes.value.splice(existingIndex, 1);
  } else {
    selectedAttributes.value.push({
      key: attribute.attributeKey,
      value: '',
      displayName: attribute.attributeDisplayName,
      displayType: attribute.attributeDisplayType,
      attributeValues: attribute.attributeValues || [],
      regexPattern: attribute.regexPattern,
      regexCue: attribute.regexCue,
    });
  }
};

const removeAttribute = index => {
  selectedAttributes.value.splice(index, 1);
};

const updateAttributeValue = (index, value) => {
  selectedAttributes.value[index].value = value;
};

const toAttributeProp = attribute => ({
  attributeKey: attribute.key,
  attributeDisplayName: attribute.displayName,
  attributeDisplayType: attribute.displayType,
  attributeValues: attribute.attributeValues,
  regexPattern: attribute.regexPattern,
  regexCue: attribute.regexCue,
  value: attribute.value,
});

const getInputType = displayType => {
  switch (displayType) {
    case 'date':
      return 'date';
    case 'number':
    case 'currency':
    case 'percent':
      return 'number';
    case 'link':
      return 'url';
    default:
      return 'text';
  }
};

const getInputPlaceholder = displayType => {
  switch (displayType) {
    case 'date':
      return t('BULK_ACTION.CUSTOM_ATTRIBUTES.ENTER_DATE_VALUE');
    case 'text':
      return t('BULK_ACTION.CUSTOM_ATTRIBUTES.ENTER_TEXT_VALUE');
    case 'number':
      return t('BULK_ACTION.CUSTOM_ATTRIBUTES.ENTER_NUMBER_VALUE');
    case 'currency':
      return t('BULK_ACTION.CUSTOM_ATTRIBUTES.ENTER_CURRENCY_VALUE');
    case 'percent':
      return t('BULK_ACTION.CUSTOM_ATTRIBUTES.ENTER_PERCENT_VALUE');
    case 'link':
      return t('BULK_ACTION.CUSTOM_ATTRIBUTES.ENTER_LINK_VALUE');
    case 'list':
      return t('BULK_ACTION.CUSTOM_ATTRIBUTES.SELECT_VALUE');
    case 'checkbox':
      return t('BULK_ACTION.CUSTOM_ATTRIBUTES.SELECT_CHECKBOX');
    default:
      return t('BULK_ACTION.CUSTOM_ATTRIBUTES.ENTER_VALUE');
  }
};

const getListOptions = attributeValues => {
  return attributeValues.map((value, index) => ({
    id: index + 1,
    name: value,
  }));
};

const goToAttributeSettings = () => {
  router.push({
    name: 'attributes_list',
    params: { accountId: route.params.accountId },
  });
  emit('close');
};

const addCustomAttributes = () => {
  if (selectedAttributes.value.length > 0) {
    emit('add', selectedAttributes.value);
  }
};

const close = () => {
  emit('close');
};
</script>

<template>
  <Teleport to="body">
    <div class="custom-attribute-actions-popover" :style="popoverStyle">
      <div class="custom-attribute-actions-header">
        <h3>
          {{
            t(
              'BULK_ACTION.CUSTOM_ATTRIBUTES.UPDATE_CUSTOM_ATTRIBUTES_FOR_CONTACTS',
              { count: selectedCount || 0 }
            )
          }}
        </h3>
        <NextButton
          icon="i-lucide-x"
          variant="ghost"
          size="xs"
          @click="close"
        />
      </div>
      <div class="custom-attribute-actions-content">
        <div class="existing-attributes-section">
          <h4 class="text-sm font-medium text-n-slate-12 mb-2">
            {{ t('BULK_ACTION.CUSTOM_ATTRIBUTES.SELECT_ATTRIBUTES_TO_UPDATE') }}
          </h4>
          <div
            v-if="!hasContactAttributes"
            class="flex flex-col gap-2 p-3 rounded bg-n-slate-2 border border-n-slate-5"
          >
            <p class="text-sm text-n-slate-11">
              {{
                t('BULK_ACTION.CUSTOM_ATTRIBUTES.NO_CONTACT_ATTRIBUTES_DEFINED')
              }}
            </p>
            <NextButton
              :label="t('BULK_ACTION.CUSTOM_ATTRIBUTES.MANAGE_IN_SETTINGS')"
              variant="link"
              size="xs"
              @click="goToAttributeSettings"
            />
          </div>
          <div v-else class="existing-attributes-list">
            <div
              v-for="attribute in attributes"
              :key="attribute.id"
              class="existing-attribute-item"
              :class="{
                selected: selectedAttributes.some(
                  attr => attr.key === attribute.attributeKey
                ),
              }"
              @click="toggleAttribute(attribute)"
            >
              <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
              <span class="attribute-icon">📝</span>
              <span class="attribute-title">{{
                attribute.attributeDisplayName
              }}</span>
              <span class="attribute-key">({{ attribute.attributeKey }})</span>
              <span
                v-if="
                  selectedAttributes.some(
                    attr => attr.key === attribute.attributeKey
                  )
                "
                class="selected-icon"
              >
                <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
                ✓
              </span>
            </div>
          </div>
        </div>

        <div
          v-if="selectedAttributes.length > 0"
          class="selected-attributes-section"
        >
          <h4 class="text-sm font-medium text-n-slate-12 mb-2">
            {{ t('BULK_ACTION.CUSTOM_ATTRIBUTES.SET_VALUES') }}
          </h4>
          <div class="selected-attributes-list">
            <div
              v-for="(attribute, index) in selectedAttributes"
              :key="attribute.key"
              class="selected-attribute-item"
            >
              <div class="attribute-header">
                <span class="attribute-name">{{ attribute.displayName }}</span>
                <NextButton
                  icon="i-lucide-x"
                  variant="ghost"
                  size="xs"
                  @click="removeAttribute(index)"
                />
              </div>
              <div class="attribute-input-container">
                <Input
                  v-if="inputTypes.includes(attribute.displayType)"
                  :model-value="attribute.value"
                  :type="getInputType(attribute.displayType)"
                  :placeholder="getInputPlaceholder(attribute.displayType)"
                  @update:model-value="
                    value => updateAttributeValue(index, value)
                  "
                />

                <MultiselectDropdown
                  v-else-if="attribute.displayType === 'list'"
                  :options="getListOptions(attribute.attributeValues)"
                  :selected-item="
                    attribute.value
                      ? {
                          id:
                            attribute.attributeValues.indexOf(attribute.value) +
                            1,
                          name: attribute.value,
                        }
                      : null
                  "
                  :has-thumbnail="false"
                  :multiselector-placeholder="
                    getInputPlaceholder(attribute.displayType)
                  "
                  :no-search-result="
                    t('CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.NO_RESULT')
                  "
                  :input-placeholder="
                    t(
                      'CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.SEARCH_INPUT_PLACEHOLDER'
                    )
                  "
                  @select="item => updateAttributeValue(index, item.name)"
                />

                <CheckboxAttribute
                  v-else-if="attribute.displayType === 'checkbox'"
                  :attribute="toAttributeProp(attribute)"
                  is-editing-view
                  @update="value => updateAttributeValue(index, value)"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="custom-attribute-actions-footer">
          <NextButton
            :label="t('BULK_ACTION.CUSTOM_ATTRIBUTES.UPDATE')"
            size="sm"
            :disabled="selectedAttributes.length === 0"
            @click="addCustomAttributes"
          />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.custom-attribute-actions-popover {
  @apply fixed z-[9999] bg-n-background border border-n-strong rounded-lg shadow-lg min-w-80 max-w-md;
}

.custom-attribute-actions-header {
  @apply flex items-center justify-between p-4 border-b border-n-strong;

  h3 {
    @apply text-sm font-medium text-n-slate-12;
  }
}

.custom-attribute-actions-content {
  @apply p-4 space-y-4;
}

.existing-attributes-section {
  @apply mb-4;
}

.existing-attributes-list {
  @apply space-y-1 max-h-32 overflow-y-auto;
}

.existing-attribute-item {
  @apply flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-n-slate-3 transition-colors;
}

.existing-attribute-item.selected {
  @apply bg-n-slate-3 border border-n-slate-6;
}

.attribute-icon {
  @apply text-sm;
}

.attribute-title {
  @apply text-sm font-medium text-n-slate-12 flex-1;
}

.attribute-key {
  @apply text-xs text-n-slate-11;
}

.selected-icon {
  @apply text-n-slate-12 font-bold;
}

.selected-attributes-section {
  @apply space-y-3;
}

.selected-attributes-list {
  @apply space-y-3;
}

.selected-attribute-item {
  @apply p-3 bg-n-slate-2 rounded border border-n-slate-5;
}

.attribute-header {
  @apply flex items-center justify-between mb-2;
}

.attribute-name {
  @apply text-sm font-medium text-n-slate-12;
}

.attribute-input-container {
  @apply w-full;
}

.custom-attribute-actions-footer {
  @apply flex justify-end pt-2 border-t border-n-strong;
}
</style>
